<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><i class="fa fa-bar-chart"></i> Báo cáo lỗ-lãi AI Token</h1>
        <p>So sánh doanh thu thu được từ khách vs chi phí thực OpenAI/DeepSeek. Tách chat vs QA.</p>
      </div>
      <ul class="app-breadcrumb breadcrumb">
        <li class="breadcrumb-item"><i class="fa fa-home fa-lg"></i></li>
        <li class="breadcrumb-item"><router-link to="/report/ai-token-pnl">AI Token P&amp;L</router-link></li>
      </ul>
    </div>

    <!-- Filter -->
    <div class="row mb-3">
      <div class="col-md-3">
        <label>Kỳ (YYYY-MM)</label>
        <input type="month" v-model="period" class="form-control" @change="load"/>
      </div>
      <div class="col-md-3">
        <label>Client (tuỳ chọn)</label>
        <select v-model="clientFilter" class="form-control" @change="load">
          <option :value="null">— Tất cả —</option>
          <option v-for="c in clientList" :key="c._id" :value="c._id">{{ c.name }}</option>
        </select>
      </div>
      <div class="col-md-3 d-flex align-items-end">
        <button class="btn btn-success" @click="exportExcel" :disabled="loading || !rows.length">
          <i class="fa fa-file-excel-o"></i> Xuất Excel
        </button>
      </div>
    </div>

    <!-- Summary cards -->
    <div class="row mb-3" v-if="summary">
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-muted small">TỔNG TOKEN</div>
          <div class="h4 mb-0">{{ summary.totalTokens.toLocaleString('vi-VN') }}</div>
          <div class="small text-muted">
            Chat {{ Math.round(summary.totalChatTokens / Math.max(1, summary.totalTokens) * 100) }}% ·
            QA {{ Math.round(summary.totalQaTokens / Math.max(1, summary.totalTokens) * 100) }}%
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3 border-info">
          <div class="text-muted small">DOANH THU</div>
          <div class="h4 mb-0 text-info">{{ summary.revenueVnd.toLocaleString('vi-VN') }}đ</div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3 border-warning">
          <div class="text-muted small">CHI PHÍ THỰC</div>
          <div class="h4 mb-0 text-warning">{{ summary.costVnd.toLocaleString('vi-VN') }}đ</div>
          <div class="small text-muted">Provider hiện tại: <b>{{ pricingRef && pricingRef.provider }}</b></div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3" :class="marginBorderClass">
          <div class="text-muted small">LÃI / LỖ</div>
          <div class="h4 mb-0" :class="marginTextClass">
            {{ summary.marginVnd.toLocaleString('vi-VN') }}đ
          </div>
          <div class="small" :class="marginTextClass">
            Margin {{ summary.marginPct }}%
          </div>
        </div>
      </div>
    </div>

    <!-- Chương trình tặng token + chi phí Trợ lý AI (KT-32990) -->
    <div class="row mb-3" v-if="summary">
      <div class="col-md-4">
        <div class="card p-3">
          <div class="text-muted small">TOKEN TẶNG MIỄN PHÍ (kỳ này)</div>
          <div class="h5 mb-0">
            {{ (summary.freeUsedTotal || 0).toLocaleString('vi-VN') }}
            <small class="text-muted">/ {{ (summary.freeGivenTotal || 0).toLocaleString('vi-VN') }} đã tặng</small>
          </div>
          <div class="small text-muted">
            Tiêu {{ freeUsedPct }}% số đã tặng · quy ra giá bán
            {{ freeUsedRevenueEquivalent.toLocaleString('vi-VN') }}đ
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3">
          <div class="text-muted small">TOKEN THEO CHỨC NĂNG</div>
          <div class="small mt-1">
            <span class="mr-2">Chat <b>{{ fmt(summary.byFeature && summary.byFeature.chat) }}</b></span>
            <span class="mr-2">Đánh giá hội thoại <b>{{ fmt(summary.byFeature && summary.byFeature.qa) }}</b></span>
            <span class="mr-2">Gọi AI <b>{{ fmt(summary.byFeature && summary.byFeature.voice) }}</b></span>
            <span class="mr-2">Đánh giá dữ liệu <b>{{ fmt(summary.byFeature && summary.byFeature.eval) }}</b></span>
            <span>Tối ưu bot <b>{{ fmt(summary.byFeature && summary.byFeature.optim) }}</b></span>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3">
          <div class="text-muted small">CHI PHÍ TRỢ LÝ AI (Callio chịu)</div>
          <div class="h5 mb-0 text-warning">{{ (summary.assistantCostVnd || 0).toLocaleString('vi-VN') }}đ</div>
          <div class="small text-muted">Miễn phí cho khách — không tính vào doanh thu</div>
        </div>
      </div>
    </div>

    <!-- Phễu sử dụng (KT-32990) -->
    <div class="card mb-3" v-if="summary && summary.funnel">
      <div class="card-body py-2">
        <div class="text-muted small mb-2">PHỄU SỬ DỤNG — kỳ {{ period }}</div>
        <div class="d-flex flex-wrap">
          <div v-for="f in funnelItems" :key="f.label" class="mr-4 mb-1">
            <div class="text-muted small">{{ f.label }}</div>
            <div class="h5 mb-0" :class="f.cls">{{ f.value.toLocaleString('vi-VN') }}</div>
          </div>
        </div>
        <div class="small text-muted mt-1">
          "Được tặng" chỉ đếm khách có phát sinh dùng AI trong kỳ — token cấp khi khách chạy, không cấp trước hàng loạt.
          Hai cột <b>Token chat</b> và <b>Token QA</b> đếm từ bản ghi hội thoại (có cả kỳ cũ);
          5 nhóm chức năng và phần tặng đếm từ bộ đếm token, chỉ có từ kỳ 2026-08 trở đi.
        </div>
      </div>
    </div>

    <!-- Bảng per client -->
    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center text-muted py-3">
          <i class="fa fa-spinner fa-spin"></i> Đang tải...
        </div>
        <table v-else class="table table-sm table-hover">
          <thead>
            <tr>
              <th>Client</th>
              <th>Gói</th>
              <th class="text-right">Token chat</th>
              <th class="text-right">Token QA</th>
              <th class="text-right">Tổng token</th>
              <th class="text-right">Tặng / đã tiêu</th>
              <th class="text-right">Gọi AI</th>
              <th class="text-right">Đánh giá DL</th>
              <th class="text-right">Tối ưu bot</th>
              <th class="text-right">Trợ lý AI</th>
              <th class="text-right">Doanh thu</th>
              <th class="text-right">Chi phí thực</th>
              <th class="text-right">Lãi / lỗ</th>
              <th class="text-right">Margin</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.clientId" :class="r.marginVnd < 0 ? 'table-danger' : ''">
              <td>
                <router-link :to="'/client/' + r.clientId">{{ r.clientName }}</router-link>
                <div v-if="r.domain" class="text-muted small">{{ r.domain }}</div>
              </td>
              <td>
                <span v-if="r.package" class="badge badge-info">{{ r.package }}</span>
                <span v-else class="text-muted small">—</span>
              </td>
              <td class="text-right">{{ r.chatTokens.toLocaleString('vi-VN') }}</td>
              <td class="text-right">{{ r.qaTokens.toLocaleString('vi-VN') }}</td>
              <td class="text-right"><b>{{ r.totalTokens.toLocaleString('vi-VN') }}</b></td>
              <td class="text-right">
                <span :class="freeExhausted(r) ? 'text-danger' : ''">{{ fmt(r.freeUsed) }}</span>
                <small class="text-muted"> / {{ fmt(r.freeAllocated) }}</small>
              </td>
              <td class="text-right">{{ fmt(r.byFeature && r.byFeature.voice) }}</td>
              <td class="text-right">{{ fmt(r.byFeature && r.byFeature.eval) }}</td>
              <td class="text-right">{{ fmt(r.byFeature && r.byFeature.optim) }}</td>
              <td class="text-right text-muted">{{ (r.assistantCostVnd || 0).toLocaleString('vi-VN') }}đ</td>
              <td class="text-right">{{ r.revenueVnd.toLocaleString('vi-VN') }}đ</td>
              <td class="text-right text-warning">{{ r.costVnd.toLocaleString('vi-VN') }}đ</td>
              <td class="text-right" :class="r.marginVnd >= 0 ? 'text-success' : 'text-danger'">
                <b>{{ r.marginVnd.toLocaleString('vi-VN') }}đ</b>
              </td>
              <td class="text-right" :class="r.marginPct >= 50 ? 'text-success' : r.marginPct >= 20 ? 'text-info' : r.marginPct >= 0 ? 'text-warning' : 'text-danger'">
                {{ r.marginPct }}%
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="14" class="text-center text-muted py-3">Không có dữ liệu cho kỳ này.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </main>
</template>

<script>
import moment from 'moment-timezone';

export default {
  name: 'AiTokenPnl',
  data() {
    return {
      loading: false,
      period: moment().format('YYYY-MM'),
      clientFilter: null,
      clientList: [],
      summary: null,
      rows: [],
      pricingRef: null,
    };
  },
  computed: {
    marginBorderClass() {
      const p = (this.summary && this.summary.marginPct) || 0;
      if (p < 0) return 'border-danger';
      if (p < 20) return 'border-warning';
      return 'border-success';
    },
    marginTextClass() {
      const p = (this.summary && this.summary.marginPct) || 0;
      if (p < 0) return 'text-danger';
      if (p < 20) return 'text-warning';
      return 'text-success';
    },
    // ===== KT-32990 — chương trình tặng token hàng tháng =====
    freeUsedPct() {
      const given = (this.summary && this.summary.freeGivenTotal) || 0;
      const used = (this.summary && this.summary.freeUsedTotal) || 0;
      return given > 0 ? Math.round(used / given * 100) : 0;
    },
    // Token tặng đã tiêu quy ra giá bán → "chương trình này đang cho đi bao nhiêu tiền"
    freeUsedRevenueEquivalent() {
      const used = (this.summary && this.summary.freeUsedTotal) || 0;
      const price = (this.pricingRef && this.pricingRef.flatPricePer1M) || 0;
      return Math.round(used * price / 1000000);
    },
    funnelItems() {
      const f = (this.summary && this.summary.funnel) || {};
      return [
        { label: 'Đã bật AI', value: f.activated || 0, cls: '' },
        { label: 'Được tặng token', value: f.granted || 0, cls: 'text-info' },
        { label: 'Có dùng', value: f.used || 0, cls: 'text-primary' },
        { label: 'Tiêu hết token tặng', value: f.freeExhausted || 0, cls: 'text-warning' },
        { label: 'Bị chặn vì hết quota', value: f.blocked || 0, cls: 'text-danger' },
        { label: 'Đang trả tiền', value: f.paying || 0, cls: 'text-success' },
      ];
    },
  },
  mounted() {
    this.loadClientList();
    this.load();
  },
  methods: {
    fmt(n) {
      return (Number(n) || 0).toLocaleString('vi-VN');
    },
    freeExhausted(r) {
      return (r.freeAllocated || 0) > 0 && (r.freeUsed || 0) >= (r.freeAllocated || 0);
    },
    async loadClientList() {
      try {
        const r = await this.$service.get('/client', { params: { limit: 1000, isChatbot: true } });
        this.clientList = (r.data && (r.data.docs || r.data)) || [];
      } catch (e) { this.clientList = []; }
    },
    async load() {
      this.loading = true;
      try {
        const r = await this.$service.get('/ai-agent/pnl', { params: { period: this.period, clientId: this.clientFilter || undefined } });
        this.summary = r.data.summary;
        this.rows = r.data.rows;
        this.pricingRef = r.data.pricingRef;
      } catch (e) {
        this.summary = null; this.rows = [];
      } finally { this.loading = false; }
    },
    exportExcel() {
      // Build CSV đơn giản (admin có thể mở Excel) — không cần lib xlsx
      const rows = [
        ['Client', 'Domain', 'Gói', 'Token chat', 'Token QA', 'Tổng token',
          'Token tặng', 'Token tặng đã tiêu', 'Token gọi AI', 'Token đánh giá dữ liệu', 'Token tối ưu bot',
          'Chi phí Trợ lý AI (đ)', 'Doanh thu (đ)', 'Chi phí thực (đ)', 'Lãi/lỗ (đ)', 'Margin %'],
        ...this.rows.map(r => [
          r.clientName, r.domain, r.package || '',
          r.chatTokens, r.qaTokens, r.totalTokens,
          r.freeAllocated || 0, r.freeUsed || 0,
          (r.byFeature && r.byFeature.voice) || 0,
          (r.byFeature && r.byFeature.eval) || 0,
          (r.byFeature && r.byFeature.optim) || 0,
          r.assistantCostVnd || 0,
          r.revenueVnd, r.costVnd, r.marginVnd, r.marginPct,
        ]),
      ];
      const csv = rows.map(row => row.map(v => {
        const s = String(v == null ? '' : v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')).join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-token-pnl-${this.period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
